/**
 * Putting the live viewer in a sheet viewport's view: its floor isolated and
 * the top-down view mode its kind renders with (plan, X-ray, ortho). Used by
 * framing (the user looks at it) and by raster export (the renderer captures
 * it); both put the viewer back afterwards.
 */

import type { ViewportSettings } from 'shared-types';
import type { RendererContext } from '../../../contexts/RendererContext';

export type PrintViewer = NonNullable<React.ContextType<typeof RendererContext>['viewer']>;

/** Top-down view per viewport kind (the first one the active persona offers). */
const VIEW_CANDIDATES: Record<ViewportSettings['kind'], string[]> = {
  // Only framing shows this one: X-ray reads best for lining up walls.
  PLAN_VECTOR: ['xray_2d', 'plan_2d_skeleton', 'orthographic_2d_top'],
  DENSITY_RASTER: ['xray_2d', 'orthographic_2d_top'],
  HEATMAP_RASTER: ['orthographic_2d_top', 'xray_2d'],
  ORTHO_RGB: ['orthographic_2d_top'],
};

export const toRadians = (deg: number) => (deg * Math.PI) / 180;

/** Sheet heading (CCW from world +Y up the page) ↔ top-down view roll: heading = −roll. */
export const headingOfRoll = (roll: number) => {
  const deg = (-roll * 180) / Math.PI;
  const wrapped = (((deg % 360) + 540) % 360) - 180;
  return Math.round(wrapped * 100) / 100;
};

/**
 * The sheet heading the viewer shows now: where the screen's up points in
 * world XY (page up = (−sin h, cos h)). Read from the camera itself, so it
 * holds however the view encodes its rotation (roll or yaw).
 */
export function headingOfViewer(viewer: PrintViewer): number {
  const camera = viewer.scene.getActiveCamera();
  camera.updateMatrixWorld();
  const e = camera.matrixWorld.elements;
  const upX = e[4];
  const upY = e[5];
  if (Math.hypot(upX, upY) < 1e-6) return headingOfRoll(viewer.scene.view.roll);
  const deg = (Math.atan2(-upX, upY) * 180) / Math.PI;
  return Math.round(deg * 100) / 100;
}

export interface ViewerViewSession {
  /** Show a viewport's floor and view; resolves once in place (false when the session ended first). */
  show: (options: { floorId: string | null; kind: ViewportSettings['kind'] }) => Promise<boolean>;
  /** Puts the floor and view from before the session back (awaitable); idempotent. */
  end: () => Promise<void>;
}

/**
 * Remembers the viewer's floor, view and camera once, switches straight from
 * one viewport's view to the next, and restores the original once at the
 * end. The camera pose is re-asserted after the switch back: the 2D → 3D
 * conversion recomputes the pivot from the floor height and a fixed pitch,
 * which would drop the user somewhere else (the DoorWindowTool does the same).
 */
export function startViewerViewSession(viewer: PrintViewer): ViewerViewSession {
  const viewManager = viewer.personaManager?.viewManager;
  const isolation = viewer.floorIsolationManager;
  const previousView = viewManager?.getCurrentView()?.id ?? null;
  const previousFloor = isolation?.getActiveFloorGroupId() ?? null;
  const mainState = viewer.renderViews[0]?.viewState;
  const pose = mainState?.captureTransientState() ?? null;
  let ended = false;
  /** Whether a show() got to change anything; an untouched session must not restore (it would undo a newer session's view). */
  let changed = false;
  let pending: Promise<unknown> = Promise.resolve();

  const setFloor = (floorId: string | null) => {
    if (!isolation) return;
    const active = isolation.getActiveFloorGroupId() ?? null;
    if (active === floorId) return;
    if (floorId) isolation.activate(floorId);
    else if (active) isolation.deactivate(active);
  };

  const show: ViewerViewSession['show'] = (options) => {
    const step = pending.then(async () => {
      if (ended) return false;
      changed = true;
      setFloor(options.floorId ?? previousFloor);
      const available = new Set((viewManager?.getAvailableViews() ?? []).map((v) => v.id));
      const viewId = VIEW_CANDIDATES[options.kind].find((id) => available.has(id));
      if (viewManager && viewId && viewManager.getCurrentView()?.id !== viewId) await viewManager.switchToView(viewId);
      // Let the switch settle (it applies the floor's saved rotation and the view's materials).
      await new Promise((resolve) => requestAnimationFrame(resolve));
      return !ended;
    });
    pending = step;
    return step;
  };

  let ending: Promise<void> | null = null;
  const end = () => {
    if (!ending) {
      ended = true;
      ending = pending
        .catch(() => undefined)
        .then(async () => {
          if (!changed) return;
          setFloor(previousFloor);
          if (viewManager && previousView && viewManager.getCurrentView()?.id !== previousView) await viewManager.switchToView(previousView);
          if (mainState && pose) {
            const now = mainState.captureTransientState();
            mainState.restoreTransientState({ ...now, cameraPosition: pose.cameraPosition, cameraTarget: pose.cameraTarget, cameraIsOrtho: pose.cameraIsOrtho, cameraRoll: pose.cameraRoll });
          }
        });
    }
    return ending;
  };

  return { show, end };
}

export interface ViewportView {
  /** Resolves once the floor and view are in place (or false when released first). */
  ready: Promise<boolean>;
  /** Puts the previous floor and view back; idempotent. */
  release: () => void;
}

/** One viewport's view for as long as the caller holds it (framing). */
export function enterViewportView(viewer: PrintViewer, options: { floorId: string | null; kind: ViewportSettings['kind'] }): ViewportView {
  const session = startViewerViewSession(viewer);
  return { ready: session.show(options), release: () => void session.end() };
}
